import {ChronoUnit} from "@js-joda/core";
import {DatasetInfo} from "api/model/dataset/DatasetInfo";
import {JodaTimeUtils} from "utils/joda-time-utils";
import {isDatasetIdentifier, standardDeviation} from "utils/utils";
import {convertValue, round2decimals} from "../../charts/utils";
import {IDataSet, IStatsRow} from "../models/interfaces";
import {AggregationType} from "../models/types";
import truncateTo = JodaTimeUtils.truncateTo;
import { PaginatedParams } from "api/model/dataset/DatasetDataInfoResponse";

export type DatasetTypes = "SECONDS" | "MINUTES" | "HOURLY" | "DAILY" | "WEEKLY" | "MONTHLY"

export const dateToTimestamp = (date: Date | undefined) => {
    if (date !== undefined) {
        return date.getTime();
    }
};

export const minusSixHours = (timestamp: number) => {
    const newDate: Date = new Date(timestamp);
    newDate.setHours(newDate.getHours() - 6);
    return newDate;
};

export const minusSixHoursDate = (date: Date) => {
    const newDate: Date = new Date(date);
    newDate.setHours(date.getHours() - 6);
    return newDate;
};

export const getUniqueObjectsByProperty = (objArr: Array<any>, property: string) => {
    const distinct = (value: any, index: number, self: any) => {
        return self.findIndex((obj: any) => obj[property] === value[property]) === index;
    };
    return objArr.filter(distinct);
};

export const convertRawData = (rawData: Array<any>) => {
    return rawData.reduce((acc: any, current: { identifier: any; data: any[] }) => {
        return (acc = {
            ...acc,
            [current.identifier]: current.data.reduce((acc2: any, current2: any) => {
                const withoutMilliseconds = `${current2.key.split(".")[0]  }Z`;
                // return acc2 = {...acc2, [current2.key]: current2.value };
                return (acc2 = {...acc2, [withoutMilliseconds]: current2.value});
            }, {}),
        });
    }, {});
};

export const removeItem = (arr: Array<any>, value: any): Array<any> => {
    const index = arr.indexOf(value);
    if (index > -1) {
        arr.splice(index, 1);
    }
    return arr;
};

export const removeItemById = ([...arr]: Array<any>, id: string): Array<any> => {
    const element = arr.find((a) => a.identifier === id);
    const index = arr.indexOf(element);
    if (index > -1) {
        arr.splice(index, 1);
    }
    return arr;
};

export const getAllCategories = (arr: Array<any>): Array<string> => {
    const uniqueArr = getUniqueObjectsByProperty([...arr], "group");
    const groupArr = uniqueArr.map((u) => u.group);
    return groupArr;
};

const keys: any = {id: "key", title: "label", children: "children"};
export const renameNestedObject = (value: any): any => {
    if (!value || typeof value !== "object") return value;
    if (Array.isArray(value)) return value.map(renameNestedObject);
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [keys[k] || k, renameNestedObject(v)]));
};

export const searchTree = (element: any, matchingKey: number): any => {
    if (element.key === matchingKey) {
        return {key: element.key, label: element.label};
    } if (element.children != null) {
        let result = null;
        for (let i = 0; result === null && i < element.children.length; i++) {
            result = searchTree(element.children[i], matchingKey);
        }
        if (result !== null) {
            result = {key: result.key, label: result.label};
        }
        return result;
    }
    return null;
};

export const getObjectKey = (object: any) => {
    const keyArr = [];
    for (const [key, value] of Object.entries(object)) {
        keyArr.push(key);
    }
    return keyArr;
};

export const getChecked = (object: any) => {
    const keyArr = [];
    for (const [key, value] of Object.entries(object)) {
        if ((value as any).checked === true) {
            keyArr.push(parseInt(key, 10));
        }
    }
    return keyArr;
};

export const isDate2BeforeDate1 = (date1: Date | undefined, date2: Date | undefined) => {
    if (date1 !== undefined && date2 !== undefined) {
        if (date2 < date1) {
            return true;
        }
    }
};

export const removeElement = (id: string) => {
    const node = document.getElementById(id);
    if (node !== null && node.parentNode !== null) {
        return node.parentNode.removeChild(node);
    }
};

export const getUniqueFlatArr = (arr: Array<any>) => {
    const cleanArr = arr.flat();
    return cleanArr.reduce((acc: Array<any>, current) => {
        if (!acc.includes(current) && isColorDarkEnough(current)) acc.push(current);
        return acc;
    }, []);
};

export const isColorDarkEnough = (color: string) => {
    color = color.substring(1); // strip #
    const rgb = parseInt(color, 16); // convert rrggbb to decimal
    const r = (rgb >> 16) & 0xff; // extract red
    const g = (rgb >> 8) & 0xff; // extract green
    const b = (rgb >> 0) & 0xff; // extract blue

    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709

    return luma < 200;
};

export const createIStatsRows = (data: Array<IDataSet>): Array<IStatsRow> => {
    return data.map((d: IDataSet) => {
        if (d.data.length > 0) {
            const valueArr: number[] = convertValue(d.data);
            const min: number = round2decimals(Math.min(...valueArr));
            const max: number = round2decimals(Math.max(...valueArr));
            const sum: number = valueArr.reduce((a: any, b: any) => a + b, 0)
            const mean: number = sum / valueArr.length;

            return {
                title: d.title.replace(/_/g, " ").toUpperCase(),
                unit: d.unit,
                pointCount: d.data.length,
                min,
                max,
                delta: round2decimals(max - min),
                sum: round2decimals(sum),
                mean: round2decimals(mean),
                std: round2decimals(standardDeviation(valueArr, mean)!)
            };
        } 
            return {
                title: d.title.replace(/_/g, " ").toUpperCase(),
                unit: d.unit,
                pointCount: d.data.length
            };
        
    })
};

export const createPieData = (dArr: Array<IDataSet>, aggregationMethod: AggregationType) => {
    const plotData: { values: Array<number>; labels: Array<string> } = {values: [], labels: []};
    dArr.forEach((d) => {
        let values: number;
        const sumValues = convertValue(d.data).reduce((acc: number, curr: number) => acc + curr, 0);
        switch (aggregationMethod) {
            case "AVG":
                values = round2decimals(sumValues / d.data.length);
                break;
            case "SUM":
                values = round2decimals(sumValues);
                break;
            default:
                values = round2decimals(sumValues);
                break;
        }
        plotData.values.push(values);
        plotData.labels.push(`${d.title.replace(/_/g, " ").toUpperCase()}, ${d.unit}`);
    });
    return plotData;
};

export const calculateStepTime = (time: number, timeframe: ChronoUnit ) => {
    switch (timeframe) {
        case ChronoUnit.SECONDS:
            return time * 1000;
        case ChronoUnit.MINUTES:
            return time * 60 * 1000;
        case ChronoUnit.HOURS:
            return time * 60 * 60 * 1000;
        case ChronoUnit.DAYS:
            return time * 24 * 60 * 60 * 1000;
        case ChronoUnit.MONTHS:
            return time;
        default:
            return time;
    }
};

export const formatDateBasedOnDataType = (type: DatasetTypes | null) => {
    const date = new Date();
    switch (type) {
        case "SECONDS":
            return truncateTo(date, ChronoUnit.SECONDS);
        case "MINUTES":
            return truncateTo(date, ChronoUnit.MINUTES);
        case "HOURLY":
            return truncateTo(date, ChronoUnit.HOURS);
        case "DAILY":
            return truncateTo(date, ChronoUnit.DAYS);
        case "WEEKLY":
            return truncateTo(date, ChronoUnit.WEEKS);
        case "MONTHLY":
            return truncateTo(date, ChronoUnit.MONTHS);
        default:
            return date;
    }
};

export const mergeUniqueObjects = (arr1: DatasetInfo[], arr2:DatasetInfo[]) => {
    const seenIdentifiers: Set<string|null> = new Set();

    const processArray = (array: DatasetInfo[]) => array.filter(obj => {
        const { identifier } = obj;
        return !seenIdentifiers.has(identifier) && seenIdentifiers.add(identifier);
    });

    return [...processArray(arr1), ...processArray(arr2)];
};

export const getSearchParams = (searchValue: string, lazyParams: {page: number, rows: number}): PaginatedParams => {
  if (isDatasetIdentifier(searchValue)) return {
    query: undefined,
    page: lazyParams.page,
    size: lazyParams.rows,
    identifiers: searchValue,
  };

  const splitValue = searchValue?.split(",");
  if (splitValue.length > 1 && splitValue.every(val => isDatasetIdentifier(val))) {
    const ids = new Set(splitValue);
    return {
      query: undefined,
      page: lazyParams.page,
      size: lazyParams.rows,
      identifiers: [...ids].join(","),
    };
  }

  return {
    query: searchValue,
    page: lazyParams.page,
    size: lazyParams.rows,
    identifiers: undefined,
  };
}
